import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import "../PurchasingUi.css";
import "./InventoryPolicyChip.css";

const OPTIONS = ["stockable", "direct_consumption"];

export default function InventoryPolicyChip({ policy, name, editable = false, onChange }) {
  const { languageId, themeId } = usePmsTheme();
  const es = languageId === "es";
  const labels = es ? ["Almacenable", "Consumo directo"] : ["Stockable", "Direct Consumption"];
  const value = policy === "direct_consumption" ? policy : "stockable";
  const title = `${es ? "Inventario" : "Inventory"}: ${name || ""}`;
  const trigger = useRef(null);
  const menu = useRef(null);
  const pending = useRef(false);
  const restoreFocus = useRef(false);
  const mounted = useRef(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useLayoutEffect(() => {
    if (!open && !saving && restoreFocus.current) {
      restoreFocus.current = false;
      trigger.current?.focus();
    }
  }, [open, saving]);
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect) return;
      const height = menu.current?.offsetHeight || 90;
      setPosition({ left: Math.max(8, Math.min(rect.left, window.innerWidth - 212)), top: rect.bottom + height + 8 < window.innerHeight ? rect.bottom + 5 : Math.max(8, rect.top - height - 5) });
    };
    place();
    menu.current?.querySelector('[aria-checked="true"]')?.focus();
    const dismiss = (event) => {
      if (!pending.current && !menu.current?.contains(event.target) && !trigger.current?.contains(event.target)) setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("pointerdown", dismiss);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("pointerdown", dismiss);
    };
  }, [open]);

  const close = () => { restoreFocus.current = true; setOpen(false); };
  const choose = async (next) => {
    if (pending.current) return;
    if (next === value) { close(); return; }
    pending.current = true; setSaving(true); setFailed(false);
    try {
      const result = await onChange(next);
      if (mounted.current) { if (result === false) setFailed(true); else close(); }
    } catch (_error) { if (mounted.current) setFailed(true); }
    finally { pending.current = false; if (mounted.current) setSaving(false); }
  };
  const keyDown = (event) => {
    if (saving) return;
    if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); close(); }
    if (event.key === "Tab") setOpen(false);
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      const buttons = [...menu.current.querySelectorAll('[role="menuitemradio"]')];
      const index = buttons.indexOf(document.activeElement);
      buttons[event.key === "Home" ? 0 : event.key === "End" ? 1 : (index + 1) % 2]?.focus();
    }
  };
  const className = `pu-policy-chip pu-policy-chip--${value}`;
  if (!editable) return <span className={className}>{labels[OPTIONS.indexOf(value)]}</span>;

  return <>
    <button ref={trigger} type="button" className={className} aria-label={`${title}: ${labels[OPTIONS.indexOf(value)]}`} aria-haspopup="menu" aria-expanded={open} aria-busy={saving} disabled={saving}
      onClick={() => { setFailed(false); setOpen(!open); }} onKeyDown={(event) => { if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); } }}>
      <span>{labels[OPTIONS.indexOf(value)]}</span><FiChevronDown aria-hidden="true" />
    </button>
    {open && createPortal(<div className="pilcore-purchasing-ui pu-policy-menu" data-theme={themeId} ref={menu} role="menu" aria-label={title} style={position} onKeyDown={keyDown}>
      {OPTIONS.map((option, index) => <button key={option} type="button" role="menuitemradio" aria-checked={value === option} disabled={saving} onClick={() => choose(option)}><span>{labels[index]}</span>{value === option && <FiCheck aria-hidden="true" />}</button>)}
      {failed && <p role="alert">{es ? "No se ha podido guardar. Inténtalo de nuevo." : "Could not save. Please try again."}</p>}
    </div>, document.body)}
  </>;
}
